/**
 * Missile patterns 11–12 — the diagonal fly-through missiles (legacy Avoid! attacks
 * #11/#12, family `missiles`; docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3).
 *
 * Legacy behaviour: a missile streaks across the play area on a diagonal over a
 * HARDCODED 2s (missiles ignore the difficulty speed ramp) and is lethal for the
 * whole flight (`missilehit=1` while flying). MISSILE_LR runs from the top-left
 * toward the bottom-right; MISSILE_RL runs from the top-right toward the
 * bottom-left.
 *
 * Unlike bombs (which fly in harmlessly then detonate), the missile is lethal the
 * entire time it's onscreen — so we give it only a brief TELEGRAPH (a tracer read as
 * it appears at the edge) and make the whole crossing the lethal ACTIVE window. It's
 * a {@link import('../types').CircleMoverHazard} travelling from `from` to `to`; the
 * stepper advances `x,y` along that line. Per legacy both windows are FIXED
 * (independent of `ctx.difficulty`). Coordinates are VIEWPORT-RELATIVE; the lane is
 * jittered from the seeded stream so the diagonal isn't identical each time.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */
import type { CircleMoverHazard, Hazard, Vec2 } from '../types';
import type { Pattern, PatternContext } from './types';

/** Legacy-hardcoded full crossing (lethal flight) duration, ms. */
const MISSILE_FLIGHT_MS = 2000;

/** Brief tracer telegraph as the missile appears at the edge, ms. */
const MISSILE_TELEGRAPH_MS = 300;

/** Missile body radius as a fraction of the smaller viewport side. */
const MISSILE_R_FRACTION = 0.05;

/** How far outside each edge the endpoints sit, as a fraction of the smaller side. */
const MISSILE_OVERSHOOT_FRACTION = 0.25;

/** Travel speed (px/s) so the missile crosses from `from`→`to` over MISSILE_FLIGHT_MS. */
function flightSpeed(from: Vec2, to: Vec2): number {
  return Math.hypot(to.x - from.x, to.y - from.y) / (MISSILE_FLIGHT_MS / 1000);
}

/**
 * Build one missile. `leftToRight` runs top-left → bottom-right; otherwise
 * top-right → bottom-left. The entry/exit y are jittered (seeded) so the diagonal
 * lane shifts run to run.
 */
function makeMissile(ctx: PatternContext, leftToRight: boolean): Hazard[] {
  const { viewport, rng, elapsedMs } = ctx;
  const { width, height } = viewport;
  const minSide = Math.min(width, height);

  const r = minSide * MISSILE_R_FRACTION;
  const overshoot = minSide * MISSILE_OVERSHOOT_FRACTION;

  // Entry near the top of the originating side, exit near the bottom of the far
  // side, both pushed offscreen by `overshoot`. The y endpoints carry seeded jitter
  // so successive missiles don't trace the identical diagonal.
  const entryY = -overshoot + rng() * (height * 0.4);
  const exitY = height + overshoot - rng() * (height * 0.4);

  const from: Vec2 = leftToRight
    ? { x: -overshoot, y: entryY }
    : { x: width + overshoot, y: entryY };
  const to: Vec2 = leftToRight
    ? { x: width + overshoot, y: exitY }
    : { x: -overshoot, y: exitY };

  const missile: CircleMoverHazard = {
    id: 0,
    family: 'missiles',
    kind: 'circleMover',
    bornMs: elapsedMs,
    telegraphMs: MISSILE_TELEGRAPH_MS,
    activeMs: MISSILE_FLIGHT_MS,
    x: from.x,
    y: from.y,
    r,
    from,
    to,
    speed: flightSpeed(from, to),
  };

  return [missile];
}

/** Attack #11 — missile left→right (top-left to bottom-right). */
export const MISSILE_LR: Pattern = {
  id: 'MISSILE_LR',
  family: 'missiles',
  spawn: (ctx) => makeMissile(ctx, true),
};

/** Attack #12 — missile right→left (top-right to bottom-left). */
export const MISSILE_RL: Pattern = {
  id: 'MISSILE_RL',
  family: 'missiles',
  spawn: (ctx) => makeMissile(ctx, false),
};
